import { useNavigate } from 'react-router-dom';
import { Container, Typography, Button, Box, Paper, List, ListItem, ListItemIcon, ListItemText } from '@mui/material';
import NotificationsActiveIcon from '@mui/icons-material/NotificationsActive';
import AssignmentIcon from '@mui/icons-material/Assignment';
import HotelIcon from '@mui/icons-material/Hotel';
import AssessmentIcon from '@mui/icons-material/Assessment';
import ScheduleIcon from '@mui/icons-material/Schedule';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import { useTranslation } from 'react-i18next';

const ExperimentalGroupWelcome = ({ setContent }) => {
    const navigate = useNavigate();
    const { t } = useTranslation();

    const handleProceedToSurvey = () => {
        // Set session storage to track that experimental welcome has been seen
        sessionStorage.setItem('experimentalGroupWelcomeSeen', 'true');

        // Set content to survey introduction
        if (setContent) {
            setContent('surveyIntroduction');
        }

        // Navigate to dashboard
        navigate('/dashboard');
    };

    return (
        <Container maxWidth="md" sx={{ mt: 8, mb: 4 }}>
            <Paper elevation={3} sx={{ p: 4, borderRadius: 2 }}>
                <Box sx={{ textAlign: 'center', mb: 4 }}>
                    <Typography variant="h3" component="h1" gutterBottom sx={{ color: '#1976d2', fontWeight: 'bold' }}>
                        {t('experimentalGroup.welcome.title', 'Welcome to the UFlow Study!')}
                    </Typography>
                    <Typography variant="h6" sx={{ color: '#666', mt: 2 }}>
                        {t('experimentalGroup.welcome.subtitle', 'Thank you for participating in our research')}
                    </Typography>
                </Box>

                <Box sx={{ my: 4, p: 3, bgcolor: '#e3f2fd', borderRadius: 2 }}>
                    <Typography variant="h5" gutterBottom sx={{ color: '#1565c0', fontWeight: 'bold' }}>
                        {t('experimentalGroup.welcome.whatToExpect', 'What to Expect During Your Participation')}
                    </Typography>

                    <List sx={{ mt: 2 }}>
                        <ListItem>
                            <ListItemIcon>
                                <AssignmentIcon sx={{ color: '#1976d2' }} />
                            </ListItemIcon>
                            <ListItemText
                                primary={t('experimentalGroup.welcome.step1', 'Initial Survey')}
                                secondary={t('experimentalGroup.welcome.step1Desc', "First, you'll complete a comprehensive survey about your work habits and preferences (approximately 10-15 minutes)")}
                            />
                        </ListItem>

                        <ListItem>
                            <ListItemIcon>
                                <NotificationsActiveIcon sx={{ color: '#1976d2' }} />
                            </ListItemIcon>
                            <ListItemText
                                primary={t('experimentalGroup.welcome.step2', 'Hourly Notifications')}
                                secondary={t('experimentalGroup.welcome.step2Desc', "During the next 2 weeks, you'll receive hourly notifications during your work hours (workdays only)")}
                            />
                        </ListItem>

                        <ListItem>
                            <ListItemIcon>
                                <ScheduleIcon sx={{ color: '#1976d2' }} />
                            </ListItemIcon>
                            <ListItemText
                                primary={t('experimentalGroup.welcome.step3', 'Performance Questions')}
                                secondary={t('experimentalGroup.welcome.step3Desc', "Each hour, you'll rate your productivity on a simple scale (takes less than 30 seconds)")}
                            />
                        </ListItem>

                        <ListItem>
                            <ListItemIcon>
                                <HotelIcon sx={{ color: '#1976d2' }} />
                            </ListItemIcon>
                            <ListItemText
                                primary={t('experimentalGroup.welcome.step4', 'Daily Sleep Tracking')}
                                secondary={t('experimentalGroup.welcome.step4Desc', "Once per day, you'll log your sleep time and sleep quality")}
                            />
                        </ListItem>

                        <ListItem>
                            <ListItemIcon>
                                <AssessmentIcon sx={{ color: '#1976d2' }} />
                            </ListItemIcon>
                            <ListItemText
                                primary={t('experimentalGroup.welcome.step5', 'Personalized Report')}
                                secondary={t('experimentalGroup.welcome.step5Desc', "After 2 weeks, you'll receive a detailed report analyzing your productivity patterns and sleep-performance relationship")}
                            />
                        </ListItem>
                    </List>
                </Box>

                <Box sx={{ my: 4, p: 3, bgcolor: '#f8fbff', borderRadius: 2, border: '1px solid #e3f2fd' }}>
                    <Typography variant="h6" gutterBottom sx={{ color: '#1565c0', fontWeight: 'bold' }}>
                        {t('experimentalGroup.welcome.importantInfo', 'Important Information:')}
                    </Typography>
                    <List dense>
                        <ListItem>
                            <ListItemIcon>
                                <CheckCircleIcon sx={{ color: '#4caf50', fontSize: 20 }} />
                            </ListItemIcon>
                            <ListItemText primary={t('experimentalGroup.welcome.info1', 'Notifications will only occur during your specified work hours')} />
                        </ListItem>
                        <ListItem>
                            <ListItemIcon>
                                <CheckCircleIcon sx={{ color: '#4caf50', fontSize: 20 }} />
                            </ListItemIcon>
                            <ListItemText primary={t('experimentalGroup.welcome.info2', 'Weekend and non-work days are excluded from notifications')} />
                        </ListItem>
                        <ListItem>
                            <ListItemIcon>
                                <CheckCircleIcon sx={{ color: '#4caf50', fontSize: 20 }} />
                            </ListItemIcon>
                            <ListItemText primary={t('experimentalGroup.welcome.info3', 'All data is confidential and used only for research purposes')} />
                        </ListItem>
                        <ListItem>
                            <ListItemIcon>
                                <CheckCircleIcon sx={{ color: '#4caf50', fontSize: 20 }} />
                            </ListItemIcon>
                            <ListItemText primary={t('experimentalGroup.welcome.info4', 'You can view your progress and patterns in real-time through the dashboard')} />
                        </ListItem>
                    </List>
                </Box>

                <Box sx={{ mt: 4, textAlign: 'center' }}>
                    <Typography variant="body1" sx={{ mb: 3, color: '#666' }}>
                        {t('experimentalGroup.welcome.readyText', "Ready to begin? Let's start with the initial survey to personalize your experience.")}
                    </Typography>
                    <Button
                        variant="contained"
                        size="large"
                        onClick={handleProceedToSurvey}
                        sx={{
                            bgcolor: '#1976d2',
                            '&:hover': {
                                bgcolor: '#1565c0'
                            },
                            px: 4,
                            py: 1.5
                        }}
                    >
                        {t('experimentalGroup.welcome.proceedButton', 'Proceed to Initial Survey')}
                    </Button>
                </Box>
            </Paper>
        </Container>
    );
};

export default ExperimentalGroupWelcome;